import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { CIDADES_CERTIFICADO } from "./data/certificadoCidades";

const ConsultoriaPage = lazy(() => import("./pages/ConsultoriaPage"));
const TelemedicinaPage = lazy(() => import("./pages/TelemedicinaPage"));
const TelemedicinaEmpresaPage = lazy(() => import("./pages/TelemedicinaEmpresaPage"));
const TelemedicinaNorteNordestePage = lazy(() => import("./pages/TelemedicinaNorteNordestePage"));
const TelemedicinaRioGrandeDoSulPage = lazy(() => import("./pages/TelemedicinaRioGrandeDoSulPage"));
const TelemedicinaLicenciamentoPage = lazy(() => import("./pages/TelemedicinaLicenciamentoPage"));
const MaquinaVendasPage = lazy(() => import("./pages/MaquinaVendasPage"));
const GestaoFinanceiraPage = lazy(() => import("./pages/GestaoFinanceiraPage"));
const PlanejamentoTributarioPage = lazy(() => import("./pages/PlanejamentoTributarioPage"));
const ProtecaoMarcaPage = lazy(() => import("./pages/ProtecaoMarcaPage"));
const SedeVirtualPage = lazy(() => import("./pages/SedeVirtualPage"));
const ReducaoTaxasPage = lazy(() => import("./pages/ReducaoTaxasPage"));
const CertificadoDigitalPage = lazy(() => import("./pages/CertificadoDigitalPage"));
const CertificadoDigitalCidadePage = lazy(() => import("./pages/CertificadoDigitalCidadePage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const SobrePage = lazy(() => import("./pages/SobrePage"));
const ContatoPage = lazy(() => import("./pages/ContatoPage"));
const PrivacidadePage = lazy(() => import("./pages/PrivacidadePage"));
const TermosPage = lazy(() => import("./pages/TermosPage"));
const MissaoPage = lazy(() => import("./pages/MissaoPage"));
const EspanolPage = lazy(() => import("./pages/EspanolPage"));
const CreditoRapidoPage = lazy(() => import("./pages/CreditoRapidoPage"));
const InstagramBioPage = lazy(() => import("./pages/InstagramBioPage"));
const TerceirizacaoComercialPage = lazy(() => import("./pages/TerceirizacaoComercialPage"));
const RecrutamentoSelecaoPage = lazy(() => import("./pages/RecrutamentoSelecaoPage"));
const LeveducaPage = lazy(() => import("./pages/LeveducaPage"));
const ProdutosPage = lazy(() => import("./pages/ProdutosPage"));
const RendaExtraPage = lazy(() => import("./pages/RendaExtraPage"));

const queryClient = new QueryClient();

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/consultoria" element={<ConsultoriaPage />} />
              <Route path="/telemedicina" element={<TelemedicinaPage />} />
              <Route path="/telemedicina/empresa" element={<TelemedicinaEmpresaPage />} />
              <Route path="/telemedicina/nortenordeste" element={<TelemedicinaNorteNordestePage />} />
              <Route path="/telemedicina/riograndedosul" element={<TelemedicinaRioGrandeDoSulPage />} />
              <Route path="/telemedicina/licenciamento" element={<TelemedicinaLicenciamentoPage />} />
              <Route path="/maquina-vendas" element={<MaquinaVendasPage />} />
              <Route path="/terceirizacao-comercial" element={<TerceirizacaoComercialPage />} />
              <Route path="/recrutamento-selecao" element={<RecrutamentoSelecaoPage />} />
              <Route path="/leveduca" element={<LeveducaPage />} />
              <Route path="/produtos" element={<ProdutosPage />} />
              <Route path="/gestao-financeira" element={<GestaoFinanceiraPage />} />
              <Route path="/planejamento-tributario" element={<PlanejamentoTributarioPage />} />
              <Route path="/protecao-marca" element={<ProtecaoMarcaPage />} />
              <Route path="/sede-virtual" element={<SedeVirtualPage />} />
              <Route path="/reducao-taxas" element={<ReducaoTaxasPage />} />
              <Route path="/certificado-digital" element={<CertificadoDigitalPage />} />
              {CIDADES_CERTIFICADO.map((c) => (
                <Route
                  key={c.slug}
                  path={`/certificado-digital-${c.slug}`}
                  element={<CertificadoDigitalCidadePage />}
                />
              ))}
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/sobre" element={<SobrePage />} />
              <Route path="/contato" element={<ContatoPage />} />
              <Route path="/politica-de-privacidade" element={<PrivacidadePage />} />
              <Route path="/termos-de-uso" element={<TermosPage />} />
              <Route path="/missao" element={<MissaoPage />} />
              <Route path="/espanol" element={<EspanolPage />} />
              <Route path="/creditorapido" element={<CreditoRapidoPage />} />
              <Route path="/i" element={<InstagramBioPage />} />
              <Route path="/rendaextra" element={<RendaExtraPage />} />
              <Route path="/rendaextra/:slug" element={<RendaExtraPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
